// A live voice call with one employee through the Gemini Live API. The browser talks to Google directly with a
// one-use token from our server (the key never comes here). Microphone audio goes up as 16 kHz PCM; the
// employee's voice comes back as 24 kHz PCM and plays as it arrives. Talking over the employee interrupts them.
// When Gemini needs company data or wants to do something it calls do_work; that goes to our server like a typed
// message (onWork), and the answer goes back to Gemini to say out loud.
import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleGenAI } from '@google/genai';
import { voiceSession, voiceUsage } from './api';

const IN_RATE = 16000;
const OUT_RATE = 24000;

// Collects microphone samples in the audio thread and hands them over in ~40 ms pieces.
const WORKLET = `
class MicTap extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) { this.buf.push(new Float32Array(ch)); this.n += ch.length; }
    if (this.n >= sampleRate / 25) {
      const out = new Float32Array(this.n); let o = 0;
      for (const b of this.buf) { out.set(b, o); o += b.length; }
      this.port.postMessage(out, [out.buffer]); this.buf = []; this.n = 0;
    }
    return true;
  }
}
registerProcessor('aio-mic-tap', MicTap);`;

function toPcm16Base64(samples, fromRate) {
  const ratio = fromRate / IN_RATE;
  const len = Math.floor(samples.length / ratio);
  const pcm = new Int16Array(len);
  for (let i = 0; i < len; i++) {
    // Average the samples that fall into this output sample (simple low-pass while downsampling).
    const a = Math.floor(i * ratio); const b = Math.min(samples.length, Math.floor((i + 1) * ratio));
    let s = 0; for (let j = a; j < b; j++) s += samples[j];
    const v = Math.max(-1, Math.min(1, b > a ? s / (b - a) : samples[a] || 0));
    pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let bin = ''; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

function fromPcm16Base64(b64) {
  const bin = atob(b64);
  const n = bin.length >> 1;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let v = bin.charCodeAt(2 * i) | (bin.charCodeAt(2 * i + 1) << 8);
    if (v >= 0x8000) v -= 0x10000;
    out[i] = v / 0x8000;
  }
  return out;
}

// state: 'idle' | 'connecting' | 'listening' | 'speaking' | 'working'
export default function useGeminiLive({ onWork, onCaption, onEnded }) {
  const [call, setCall] = useState({ agentId: null, state: 'idle', muted: false, startedAt: null });
  const r = useRef({});
  const cb = useRef({});
  cb.current = { onWork, onCaption, onEnded };

  const setState = (state) => setCall((c) => (c.agentId ? { ...c, state } : c));

  const stopPlayback = () => {
    const x = r.current;
    (x.playing || []).forEach((s) => { try { s.stop(); } catch (_) { /* already ended */ } });
    x.playing = [];
    x.playAt = 0;
  };

  const play = (b64) => {
    const x = r.current;
    if (!x.outCtx) return;
    const data = fromPcm16Base64(b64);
    const buf = x.outCtx.createBuffer(1, data.length, OUT_RATE);
    buf.copyToChannel(data, 0);
    const src = x.outCtx.createBufferSource();
    src.buffer = buf;
    src.connect(x.outCtx.destination);
    const at = Math.max(x.outCtx.currentTime + 0.03, x.playAt || 0);
    src.start(at);
    x.playAt = at + buf.duration;
    x.playing = [...(x.playing || []), src];
    src.onended = () => {
      x.playing = (x.playing || []).filter((s) => s !== src);
      if (!x.playing.length && x.working === 0) setState('listening');
    };
    setState('speaking');
  };

  const end = useCallback((reason) => {
    const x = r.current;
    if (!x.agentId) return;
    const { agentId, startedAt } = x;
    x.agentId = null;
    try { x.session && x.session.close(); } catch (_) { /* closed */ }
    try { x.node && x.node.disconnect(); } catch (_) { /* gone */ }
    (x.stream ? x.stream.getTracks() : []).forEach((t) => t.stop());
    stopPlayback();
    try { x.inCtx && x.inCtx.close(); } catch (_) { /* closed */ }
    try { x.outCtx && x.outCtx.close(); } catch (_) { /* closed */ }
    r.current = {};
    setCall({ agentId: null, state: 'idle', muted: false, startedAt: null });
    if (startedAt) voiceUsage(agentId, Math.round((Date.now() - startedAt) / 1000)).catch(() => {});
    if (cb.current.onEnded) cb.current.onEnded(agentId, reason);
  }, []);

  const start = useCallback(async (agentId) => {
    if (r.current.agentId) end();
    const x = { agentId, working: 0, playing: [], caption: { boss: '', agent: '' } };
    r.current = x;
    setCall({ agentId, state: 'connecting', muted: false, startedAt: null });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      if (r.current !== x) { stream.getTracks().forEach((t) => t.stop()); return; }
      x.stream = stream;
      const { token, model, config } = await voiceSession(agentId);
      if (r.current !== x) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      x.outCtx = new AC({ sampleRate: OUT_RATE });
      x.inCtx = new AC();
      // Started after an await, so some browsers (Safari) create them paused.
      await Promise.all([x.outCtx.resume(), x.inCtx.resume()]).catch(() => {});
      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
      await x.inCtx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);

      const ai = new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: 'v1alpha' } });
      x.session = await ai.live.connect({
        model,
        config,
        callbacks: {
          onopen: () => {},
          onmessage: (msg) => onMessage(x, msg),
          onerror: (e) => { if (r.current === x) end(`The call dropped: ${(e && e.message) || 'connection error'}`); },
          onclose: (e) => { if (r.current === x) end(e && e.reason ? `The call ended: ${e.reason}` : null); },
        },
      });
      if (r.current !== x) { try { x.session.close(); } catch (_) { /* closed */ } return; }

      const source = x.inCtx.createMediaStreamSource(stream);
      x.node = new AudioWorkletNode(x.inCtx, 'aio-mic-tap');
      x.node.port.onmessage = (e) => {
        if (r.current !== x || x.muted || !x.session) return;
        try { x.session.sendRealtimeInput({ audio: { data: toPcm16Base64(e.data, x.inCtx.sampleRate), mimeType: `audio/pcm;rate=${IN_RATE}` } }); } catch (_) { /* closing */ }
      };
      source.connect(x.node);
      x.startedAt = Date.now();
      setCall({ agentId, state: 'listening', muted: false, startedAt: x.startedAt });
    } catch (err) {
      const msg = err && err.name === 'NotAllowedError'
        ? 'The microphone is blocked. Allow it in the browser (the icon left of the address bar), then call again.'
        : (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Could not start the call.';
      if (r.current === x) end(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end]);

  function onMessage(x, msg) {
    if (r.current !== x) return;
    const sc = msg.serverContent;
    if (sc) {
      if (sc.interrupted) stopPlayback();
      for (const p of (sc.modelTurn && sc.modelTurn.parts) || []) if (p.inlineData && p.inlineData.data) play(p.inlineData.data);
      if (sc.inputTranscription && sc.inputTranscription.text) {
        x.caption.boss += sc.inputTranscription.text;
        if (cb.current.onCaption) cb.current.onCaption(x.agentId, 'boss', x.caption.boss, false);
      }
      if (sc.outputTranscription && sc.outputTranscription.text) {
        if (x.caption.boss) { if (cb.current.onCaption) cb.current.onCaption(x.agentId, 'boss', x.caption.boss, true); x.caption.boss = ''; }
        x.caption.agent += sc.outputTranscription.text;
        if (cb.current.onCaption) cb.current.onCaption(x.agentId, 'agent', x.caption.agent, false);
      }
      if (sc.turnComplete || sc.interrupted) {
        if (x.caption.boss && cb.current.onCaption) cb.current.onCaption(x.agentId, 'boss', x.caption.boss, true);
        if (x.caption.agent && cb.current.onCaption) cb.current.onCaption(x.agentId, 'agent', x.caption.agent, true);
        x.caption = { boss: '', agent: '' };
      }
    }
    if (msg.toolCall && msg.toolCall.functionCalls) {
      for (const fc of msg.toolCall.functionCalls) {
        const request = String((fc.args && fc.args.request) || '').trim();
        x.working += 1;
        setState('working');
        Promise.resolve(cb.current.onWork ? cb.current.onWork(x.agentId, request) : { result: 'Not available.' })
          .catch(() => ({ result: 'It did not work: the server could not be reached.' }))
          .then((out) => {
            x.working -= 1;
            if (r.current !== x || !x.session) return;
            try { x.session.sendToolResponse({ functionResponses: [{ id: fc.id, name: fc.name, response: { result: String((out && out.result) || 'Done.') } }] }); } catch (_) { /* closed */ }
            if (!x.working) setState('listening');
          });
      }
    }
    if (msg.goAway) end('The call reached its time limit. Call again to continue.');
  }

  const toggleMute = () => {
    const x = r.current;
    if (!x.agentId) return;
    x.muted = !x.muted;
    setCall((c) => ({ ...c, muted: x.muted }));
  };

  useEffect(() => () => end(), [end]);

  return { call, start, end, toggleMute };
}

export const liveCallSupported = () => !!(typeof window !== 'undefined' && window.AudioWorkletNode && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
